/* import __COLOCATED_TEMPLATE__ from './suggestion-title-composer.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { taskFor } from 'ember-concurrency-ts';
import type IntlService from 'ember-intl/services/intl';
import type ContentReviewRequest from 'embercom/models/content-service/content-review-request';
import { ReviewType } from 'embercom/models/content-service/content-review-request';
import { EntityType } from 'embercom/models/data/entity-types';
import { type LocalizedKnowledgeContent } from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type SuggestionService from 'embercom/services/suggestion-service';

interface Args {
  activeContent: LocalizedKnowledgeContent;
  reviewRequest: ContentReviewRequest[];
}

export default class SuggestionTitleComposer extends Component<Args> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare suggestionService: SuggestionService;
  @service declare permissionsService: $TSFixMe;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
  @tracked editedTitle: string | null = null;
  @tracked isFocused = false;

  get app(): $TSFixMe {
    return this.appService.app;
  }

  get entityType() {
    return this.args.activeContent.entityType;
  }

  get typeTitle() {
    if (this.args.activeContent.defaultTitle) {
      return this.args.activeContent.defaultTitle;
    }

    // Use standalone translations for EntityType.ArticleContent (120) when in standalone mode
    if (this.app.canUseStandalone && this.entityType === EntityType.ArticleContent) {
      return this.intl.t(`knowledge-hub.content-type-stamp.standalone.${this.entityType}`);
    }

    return this.intl.t(`knowledge-hub.content-type-stamp.content-type.${this.entityType}`);
  }

  get currentTitle() {
    return this.editedTitle ?? this.args.activeContent.latestVersion?.versionTitle;
  }

  set currentTitle(value: string | undefined) {
    this.editedTitle = value ?? null;
    this.suggestionService.hasDirtyContent = true;
    this.suggestionService.title = this.editedTitle;

    if (this.isCreateSnippet) {
      taskFor(this.suggestionService.autoSaveContentTask).perform();
    }
  }

  get isCreateSnippet() {
    if (this.args.activeContent.entityType === EntityType.ContentSnippet) {
      return this.args.reviewRequest.every(
        (request) => request.get('reviewType') === ReviewType.CREATE_CONTENT,
      );
    }
    return false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ContentReview::SuggestionTitleComposer': typeof SuggestionTitleComposer;
    'knowledge-hub/content-editor/content-review/suggestion-title-composer': typeof SuggestionTitleComposer;
  }
}
